import { useEffect, useState, useCallback, useRef, ReactNode } from "react";
import styled from "styled-components";
import SntCloseIcon from "@icons/SntCloseIcon";
import AwesomeIcon from "@icons/AwesomeIcon";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

interface WindowPosition {
  x: number;
  y: number;
}

interface WindowSize {
  width: number;
  height: number;
}

interface FloatingWindowProps {
  show: boolean;
  onClose: () => void;
  title: ReactNode;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  defaultWidth?: number;
  defaultHeightPercent?: number;
  minWidth?: number;
  minHeight?: number;
}

const WINDOW_MAXIMIZED_PERCENT = 0.7;

const StyledFloatingWindow = styled.div<{ $x: number; $y: number; $width: number; $height: number }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 1050;
  overflow: hidden;

  .drag-handle {
    cursor: move;
    user-select: none;
  }

  .resize-handle {
    position: absolute;
    width: 12px;
    height: 12px;
    bottom: 0;
    right: 0;
    cursor: nwse-resize;

    &::before {
      content: "";
      position: absolute;
      right: 3px;
      bottom: 3px;
      width: 8px;
      height: 8px;
      border-right: 2px solid var(--bs-secondary);
      border-bottom: 2px solid var(--bs-secondary);
      opacity: 0.5;
    }

    &:hover::before {
      opacity: 1;
    }
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--bs-border-color);
  background-color: var(--bs-body-bg);

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-center {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;

    .title-text {
      font-weight: 400;

      strong {
        font-weight: 600;
      }
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .header-button {
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    color: var(--bs-body-color);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
      background-color: var(--bs-light);
    }
  }
`;

const FloatingWindow = ({
  show,
  onClose,
  title,
  headerLeft,
  headerRight,
  children,
  defaultWidth = 420,
  defaultHeightPercent = 0.6,
  minWidth = 350,
  minHeight = 300,
}: FloatingWindowProps) => {
  const getDefaultPosition = useCallback((): WindowPosition => {
    const viewportHeight = window.innerHeight;
    const windowHeight = Math.round(viewportHeight * defaultHeightPercent);
    return {
      x: window.innerWidth - defaultWidth - 20,
      y: viewportHeight - windowHeight - 20,
    };
  }, [defaultWidth, defaultHeightPercent]);

  const getDefaultSize = useCallback((): WindowSize => {
    const viewportHeight = window.innerHeight;
    return {
      width: defaultWidth,
      height: Math.round(viewportHeight * defaultHeightPercent),
    };
  }, [defaultWidth, defaultHeightPercent]);

  const [windowPosition, setWindowPosition] = useState<WindowPosition>(getDefaultPosition);
  const [windowSize, setWindowSize] = useState<WindowSize>(getDefaultSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const resizeStart = useRef<{ x: number; y: number; width: number; height: number }>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  // Reset position when window is shown
  useEffect(() => {
    if (show) {
      setWindowPosition(getDefaultPosition());
      setWindowSize(getDefaultSize());
      setIsMaximized(false);
    }
  }, [show, getDefaultPosition, getDefaultSize]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;

    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - windowPosition.x,
      y: e.clientY - windowPosition.y,
    };
    e.preventDefault();
  }, [windowPosition]);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newX = Math.max(0, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - windowSize.width));
    const newY = Math.max(0, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - windowSize.height));

    setWindowPosition({ x: newX, y: newY });
  }, [isDragging, windowSize]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    setIsMaximized(false);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: windowSize.width,
      height: windowSize.height,
    };
    e.preventDefault();
    e.stopPropagation();
  }, [windowSize]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeStart.current.x;
    const deltaY = e.clientY - resizeStart.current.y;

    const newWidth = Math.max(minWidth, resizeStart.current.width + deltaX);
    const newHeight = Math.max(minHeight, resizeStart.current.height + deltaY);

    const maxWidth = window.innerWidth - windowPosition.x;
    const maxHeight = window.innerHeight - windowPosition.y;

    setWindowSize({
      width: Math.min(newWidth, maxWidth),
      height: Math.min(newHeight, maxHeight),
    });
  }, [isResizing, windowPosition, minWidth, minHeight]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Toggle maximize/restore
  const toggleMaximize = useCallback(() => {
    if (isMaximized) {
      setWindowPosition(getDefaultPosition());
      setWindowSize(getDefaultSize());
      setIsMaximized(false);
    } else {
      const maxWidth = Math.round(window.innerWidth * WINDOW_MAXIMIZED_PERCENT);
      const maxHeight = Math.round(window.innerHeight * WINDOW_MAXIMIZED_PERCENT);
      const centeredX = Math.round((window.innerWidth - maxWidth) / 2);
      const centeredY = Math.round((window.innerHeight - maxHeight) / 2);

      setWindowPosition({ x: centeredX, y: centeredY });
      setWindowSize({ width: maxWidth, height: maxHeight });
      setIsMaximized(true);
    }
  }, [isMaximized, getDefaultPosition, getDefaultSize]);

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      return () => {
        window.removeEventListener("mousemove", handleDragMove);
        window.removeEventListener("mouseup", handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeEnd);
      return () => {
        window.removeEventListener("mousemove", handleResizeMove);
        window.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  if (!show) return null;

  return (
    <StyledFloatingWindow
      $x={windowPosition.x}
      $y={windowPosition.y}
      $width={windowSize.width}
      $height={windowSize.height}
    >
      <StyledHeader className="drag-handle" onMouseDown={handleDragStart}>
        <div className="header-left">
          {headerLeft}
        </div>

        <div className="header-center">
          <span className="title-text">
            {title}
          </span>
        </div>

        <div className="header-right">
          {headerRight}
          <button
            className="header-button"
            onClick={toggleMaximize}
            title={isMaximized ? "Restore" : "Maximize"}
          >
            <AwesomeIcon icon={isMaximized ? faCompress : faExpand} />
          </button>
          <button className="header-button" onClick={onClose}>
            <SntCloseIcon size={16} />
          </button>
        </div>
      </StyledHeader>

      <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {children}
      </div>
      <div className="resize-handle" onMouseDown={handleResizeStart} />
    </StyledFloatingWindow>
  );
};

export default FloatingWindow;
